/**
 * SF-4794: pick one of our open Jira epics in the Add item window. The list is newest first,
 * filtered by the search (debounced), and loads the next page when its bottom scrolls into
 * view, with a "Load more" button for keyboards and browsers without IntersectionObserver.
 * Picking only hands the epic up: the list never touches what the person typed.
 *
 * SF-5080: any open ticket can be picked now, not only epics, so each row shows its type.
 */
import { useEffect, useRef, useState } from 'react';
import { ExportOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Space, Spin, Tag, Typography, theme } from 'antd';
import { formatShortDate } from '../../../utils/date';
import {
  prioritiesErrorCode,
  prioritiesErrorMessage,
  useJiraEpics,
  type JiraEpic,
} from '../api/prioritiesClient';
import { LoadError, RefreshWarning } from './LoadError';

/** Wait this long after the last keystroke before searching, as the other admin searches do. */
const SEARCH_DEBOUNCE_MS = 300;
/** Room for about six rows; the rest scrolls. */
const LIST_HEIGHT_PX = 320;

/**
 * Pages adding no new epic (empty, or only repeats) to load on our own before waiting for Load more. Each page is one Jira search
 * through sf-api (up to its 8 s timeout), so three keeps the wait bounded.
 */
const MAX_EMPTY_PAGES_IN_A_ROW = 3;

const NOT_CONFIGURED = 'JIRA_NOT_CONFIGURED';

interface PickerProps {
  onPick: (epic: JiraEpic) => void;
  listId: string;
  /** SF-5087: tickets the form already holds, and the item being edited (its own are not taken). */
  pickedKeys?: string[];
  ownItemId?: string;
  /** SF-5087: true once the form holds as many tickets as allowed. */
  full?: boolean;
}

export const JiraEpicPicker: React.FC<PickerProps> = ({
  onPick,
  listId,
  pickedKeys = [],
  ownItemId,
  full = false,
}) => {
  const { token } = theme.useToken();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setQ(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  // A new search is a new query key, so it starts from its own first page (a search used
  // before in this window shows its cached pages while they refresh).
  const epicsQuery = useJiraEpics(q, true, listId);
  const { isFetching, isError, fetchNextPage } = epicsQuery;
  const pages = epicsQuery.data?.pages ?? [];
  const pageParams = epicsQuery.data?.pageParams ?? [];
  // A cursor already used would only load the same page again, so it ends the list.
  const nextCursor = pages.at(-1)?.nextCursor ?? null;
  const hasMore =
    !!epicsQuery.hasNextPage && nextCursor !== null && !pageParams.includes(nextCursor);
  // Rows de-duplicated by key. sf-api filters each Jira page, so a page can be empty, or repeat
  // epics already shown, and still have more after it. Count the pages at the end that added
  // nothing new: those are loaded on our own only a few times in a row (then Load more).
  const seen = new Set<string>();
  const epics: JiraEpic[] = [];
  let trailingEmpty = 0;
  for (const page of pages) {
    const before = epics.length;
    for (const epic of page.epics) {
      if (!seen.has(epic.key)) {
        seen.add(epic.key);
        epics.push(epic);
      }
    }
    trailingEmpty = epics.length === before ? trailingEmpty + 1 : 0;
  }

  // One next-page fetch at a time for this search: the observer can fire again before
  // isFetching updates. Keyed to the search, so a page still loading for an old search never
  // holds up the new one.
  const loadingFor = useRef<string | null>(null);
  const fetchMore = useRef(() => {});
  fetchMore.current = () => {
    if (!hasMore || isFetching || loadingFor.current === q) return;
    const forQ = q;
    loadingFor.current = forQ;
    void fetchNextPage().finally(() => {
      if (loadingFor.current === forQ) loadingFor.current = null;
    });
  };

  // A Load more press while the list is refreshing (reopening the window refetches the loaded
  // pages) is kept for this search and carried out once the refresh lands, instead of being
  // dropped. Only a press is kept: automatic loading re-checks its own conditions instead. The
  // kept press is dropped when the search changes, the refresh fails, or nothing is left.
  const [pressedFor, setPressedFor] = useState<string | null>(null);
  const pressLoadMore = () => {
    if (isFetching) setPressedFor(q);
    else fetchMore.current();
  };
  useEffect(() => {
    if (pressedFor !== null && (pressedFor !== q || isError || !hasMore)) setPressedFor(null);
  }, [pressedFor, q, isError, hasMore]);

  // Load on our own while the bottom of the list is in view, or past empty pages, but stop
  // after a failure and after a few empty pages in a row (then Load more carries on).
  const sentinelVisible = useRef(false);
  const autoLoad = useRef(() => {});
  autoLoad.current = () => {
    if (isError || trailingEmpty >= MAX_EMPTY_PAGES_IN_A_ROW) return;
    if (sentinelVisible.current || trailingEmpty > 0) fetchMore.current();
  };

  // A state, not a ref: the list (and its sentinel) unmounts while a new search loads, and the
  // observer must follow the new one.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    sentinelVisible.current = false;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        sentinelVisible.current = entries.at(-1)?.isIntersecting ?? false;
        if (sentinelVisible.current) autoLoad.current();
      },
      { root: sentinel.parentElement },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel]);

  // The observer only reports the bottom crossing into view, so when a page lands and the
  // bottom is still in view (a short page), nothing new would arrive: check again once the
  // search's fetch has settled.
  const pageCount = pages.length;
  useEffect(() => {
    if (isFetching) return;
    if (pressedFor === q && !isError && hasMore) {
      setPressedFor(null);
      fetchMore.current();
    } else autoLoad.current();
  }, [q, pageCount, isFetching, isError, pressedFor, hasMore]);

  const refreshWarning = () => {
    if (!isError) return null;
    if (prioritiesErrorCode(epicsQuery.error) === NOT_CONFIGURED) {
      // Not set up is an answer, not a blip: no Retry.
      return (
        <Alert
          type="warning"
          showIcon
          message={`${prioritiesErrorMessage(epicsQuery.error, '')} Showing what was loaded before.`}
        />
      );
    }
    return (
      <RefreshWarning
        error={epicsQuery.error}
        fallback="The Jira tickets could not be refreshed."
        onRetry={epicsQuery.refetch}
      />
    );
  };

  const renderBody = () => {
    if (epicsQuery.isError && !epicsQuery.data) {
      if (prioritiesErrorCode(epicsQuery.error) === NOT_CONFIGURED) {
        return (
          <Alert
            type="info"
            showIcon
            message="The Jira list is not set up yet. You can still add the item by hand."
          />
        );
      }
      return (
        <LoadError
          error={epicsQuery.error}
          fallback="The Jira tickets could not be loaded. You can still add the item by hand."
          onRetry={epicsQuery.refetch}
        />
      );
    }
    if (epicsQuery.isLoading) {
      return (
        <Space>
          <Spin size="small" />
          <Typography.Text type="secondary">Loading Jira tickets…</Typography.Text>
        </Space>
      );
    }
    return (
      <>
        {refreshWarning()}
        {/* Nothing matched only once a successful answer has no more pages. */}
        {epics.length === 0 && !hasMore && !isError && (
          <Typography.Text type="secondary">No open tickets match</Typography.Text>
        )}
        <div
          style={{
            maxHeight: LIST_HEIGHT_PX,
            overflowY: 'auto',
            border: epics.length > 0 ? `1px solid ${token.colorBorderSecondary}` : undefined,
            borderRadius: token.borderRadius,
          }}
        >
          {epics.map((epic, index) => (
            <div
              key={epic.key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '4px 8px 4px 0',
                borderTop: index > 0 ? `1px solid ${token.colorBorderSecondary}` : undefined,
              }}
            >
              {/* The row itself picks the epic; the link sits beside it, since a link cannot go inside a button. */}
              <Button
                type="text"
                disabled={
                  full ||
                  pickedKeys.includes(epic.key) ||
                  (!!epic.itemId && epic.itemId !== ownItemId)
                }
                aria-label={`Pick ${epic.key} ${epic.title}`}
                onClick={() => onPick(epic)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: 'auto',
                  display: 'block',
                  textAlign: 'left',
                  whiteSpace: 'normal',
                }}
              >
                <div style={{ display: 'flex', gap: 8 }}>
                  <Typography.Text strong style={{ flex: 'none', minWidth: 64 }}>
                    {epic.key}
                  </Typography.Text>
                  <div style={{ minWidth: 0 }}>
                    {epic.issueType && <Tag style={{ marginRight: 6 }}>{epic.issueType}</Tag>}
                    {/* A long word or pasted link in a title wraps instead of running under the Open link. */}
                    <div style={{ overflowWrap: 'anywhere' }}>{epic.title}</div>
                    <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                      Updated {formatShortDate(epic.updatedAt)}
                      {pickedKeys.includes(epic.key)
                        ? ' · Picked'
                        : epic.itemId && epic.itemId !== ownItemId && ' · Already on the list'}
                    </Typography.Text>
                  </div>
                </div>
              </Button>
              <a
                href={epic.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${epic.key} in Jira`}
                title="Open in Jira"
                style={{ flex: 'none', paddingTop: 4, fontSize: token.fontSizeSM }}
              >
                Open <ExportOutlined />
              </a>
            </div>
          ))}
          <div ref={setSentinel} />
        </div>
        {hasMore && (
          <Button
            size="small"
            onClick={pressLoadMore}
            loading={epicsQuery.isFetchingNextPage || pressedFor === q}
            aria-busy={epicsQuery.isFetchingNextPage || pressedFor === q}
          >
            Load more
          </Button>
        )}
      </>
    );
  };

  return (
    // A plain column, not a Space: Space items shrink to their content, which left the rows ragged.
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', marginBottom: 16 }}
    >
      <Typography.Text strong>Jira ticket</Typography.Text>
      <Input
        aria-label="Search Jira tickets"
        placeholder="Search by title or ticket number"
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {renderBody()}
    </div>
  );
};
